import React, { useState, useEffect, useRef } from 'react';
import { ref, get, push, set, update, remove } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { X, Plus, Trash2, Pencil, Camera, Mic, Square, Save } from 'lucide-react';
import Button from '../../../ui-kit/Button';
import { TicketService } from '../services/ticketService';

// Stored under userFilters/ (staff-writable node) because the RTDB rules have
// no dedicated canned node and rules deploys are gated.
export const CANNED_PATH = 'userFilters/cannedResponses';

const isImg = (t) => String(t || '').startsWith('image/');
const isAud = (t) => String(t || '').startsWith('audio/');

/**
 * Canned Responses & FAQ manager: team-wide saved replies with categories,
 * image / voice attachments, and {placeholders} substituted on insert.
 */
const CannedManagerModal = ({ onClose, onChanged, currentUser, inline = false }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // {id?} being edited/created
    const [f, setF] = useState({ title: '', category: '', shortcut: '', text: '', attachments: [] });
    const slug = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 16);
    const [busy, setBusy] = useState(false);
    const [recording, setRecording] = useState(false);
    const [recSecs, setRecSecs] = useState(0);
    const recRef = useRef(null);
    const recTimer = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const s = await get(ref(db, CANNED_PATH));
            setItems(s.exists() ? Object.entries(s.val()).map(([id, c]) => ({ id, ...c })) : []);
        } catch (e) { alert('Failed to load canned responses: ' + e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const startEdit = (item) => {
        setEditing(item || {});
        setF(item ? { title: item.title || '', category: item.category || '', shortcut: item.shortcut || '', text: item.text || '', attachments: item.attachments || [] }
            : { title: '', category: '', shortcut: '', text: '', attachments: [] });
    };

    const saveItem = async () => {
        if (!f.text.trim() && !f.attachments.length) { alert('Write the response text (or attach a file).'); return; }
        const shortcut = slug(f.shortcut);
        if (shortcut && items.some(it => it.shortcut === shortcut && it.id !== editing?.id)) { alert(`Shortcut /${shortcut} is already used by another response.`); return; }
        setBusy(true);
        try {
            const entry = {
                title: f.title.trim() || f.text.slice(0, 40),
                category: f.category.trim(),
                shortcut,
                text: f.text,
                attachments: f.attachments,
                createdBy: currentUser?.displayName || currentUser?.email || 'agent',
                updatedAt: Date.now(),
            };
            if (editing?.id) await update(ref(db, `${CANNED_PATH}/${editing.id}`), entry);
            else { const r = push(ref(db, CANNED_PATH)); await set(r, { ...entry, createdAt: Date.now() }); }
            setEditing(null);
            await load();
            onChanged && onChanged();
        } catch (e) { alert('Save failed: ' + e.message); }
        finally { setBusy(false); }
    };

    const deleteItem = async (item) => {
        if (!window.confirm(`Delete canned response "${item.title}"?`)) return;
        try { await remove(ref(db, `${CANNED_PATH}/${item.id}`)); await load(); onChanged && onChanged(); }
        catch (e) { alert('Delete failed: ' + e.message); }
    };

    const attachFiles = async (fileList) => {
        setBusy(true);
        try {
            const ups = [];
            for (const file of fileList) ups.push(await TicketService.uploadCannedAsset(file));
            setF(s => ({ ...s, attachments: [...s.attachments, ...ups] }));
        } catch (e) { alert('Upload failed: ' + e.message); }
        finally { setBusy(false); }
    };

    const startRec = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            const chunks = [];
            mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
            mr.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
                const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type });
                await attachFiles([file]);
            };
            mr.start(); recRef.current = mr;
            setRecording(true); setRecSecs(0);
            recTimer.current = setInterval(() => setRecSecs(s => s + 1), 1000);
        } catch { alert('Microphone access denied.'); }
    };
    const stopRec = () => { try { recRef.current?.stop(); } catch { /* noop */ } clearInterval(recTimer.current); setRecording(false); };

    const grouped = items.reduce((m, it) => { const k = it.category || 'General'; (m[k] = m[k] || []).push(it); return m; }, {});

    const Wrapper = ({ children }) => inline
        ? <div className="space-y-3">{children}</div>
        : (
            <div className="fixed inset-0 z-[55] flex items-center justify-center p-3" onClick={onClose}>
                <div className="absolute inset-0 bg-black/60" />
                <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>{children}</div>
            </div>
        );

    return (
        <Wrapper>
                <div className={inline ? 'flex items-center justify-between' : 'px-5 py-4 border-b border-slate-100 bg-purple-50/70 flex items-center justify-between'}>
                    {!inline && (
                        <div>
                            <h3 className="font-bold text-slate-800">⚡ Canned Responses & FAQ</h3>
                            <p className="text-[11px] text-slate-400">Team-wide saved replies · type <span className="font-mono font-bold">/</span> in any composer to insert · supports image & voice attachments</p>
                        </div>
                    )}
                    {inline && <span className="text-xs text-slate-500">{items.length} saved response{items.length === 1 ? '' : 's'}</span>}
                    <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => startEdit(null)}><Plus size={14} /> New</Button>
                        {!inline && <button onClick={onClose} className="p-1.5 hover:bg-white rounded-full"><X size={18} /></button>}
                    </div>
                </div>

                <div className={inline ? 'space-y-4' : 'flex-1 overflow-y-auto p-4 space-y-4'}>
                    {editing !== null && (
                        <div className="bg-purple-50/60 border-2 border-purple-200 rounded-xl p-3.5 space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                                <input value={f.title} onChange={e => setF(s => ({ ...s, title: e.target.value }))} placeholder="Title (shown in the / picker)"
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none bg-white" />
                                <input value={f.category} onChange={e => setF(s => ({ ...s, category: e.target.value }))} placeholder="Category (e.g. Refunds, FAQ, Delivery)"
                                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none bg-white" />
                                <div className="flex items-center border border-slate-200 rounded-lg bg-white px-2">
                                    <span className="font-mono text-slate-400 text-sm">/</span>
                                    <input value={f.shortcut} onChange={e => setF(s => ({ ...s, shortcut: slug(e.target.value) }))} placeholder="shortcut (e.g. rfd)"
                                        className="flex-1 px-1.5 py-2 text-sm outline-none font-mono bg-transparent" />
                                </div>
                            </div>
                            <textarea value={f.text} onChange={e => setF(s => ({ ...s, text: e.target.value }))} rows={4}
                                placeholder={'Response text…\nPlaceholders auto-filled on insert: {customerName} {ticketNumber} {trackingLink} {agentName}'}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm outline-none resize-none bg-white" />
                            {f.attachments.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {f.attachments.map((a, i) => (
                                        <span key={i} className="inline-flex items-center gap-1.5 text-[11px] bg-white border border-slate-200 rounded-lg px-2 py-1">
                                            {isImg(a.type) ? <img src={a.url} alt="" className="h-8 rounded" /> : isAud(a.type) ? '🎤' : '📎'}
                                            <span className="truncate max-w-[120px]">{a.name}</span>
                                            <button onClick={() => setF(s => ({ ...s, attachments: s.attachments.filter((_, j) => j !== i) }))} className="text-slate-400 hover:text-red-500"><X size={11} /></button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="flex items-center gap-2 flex-wrap">
                                <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-bold cursor-pointer hover:bg-slate-50">
                                    <Camera size={13} /> Image
                                    <input type="file" accept="image/*" multiple className="hidden" onChange={e => { attachFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                                </label>
                                <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-bold cursor-pointer hover:bg-slate-50">
                                    🎵 Audio file
                                    <input type="file" accept="audio/*" className="hidden" onChange={e => { attachFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                                </label>
                                {!recording ? (
                                    <button type="button" onClick={startRec} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50">
                                        <Mic size={13} /> Record voice
                                    </button>
                                ) : (
                                    <button type="button" onClick={stopRec} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold animate-pulse">
                                        <Square size={11} /> Stop · {recSecs}s
                                    </button>
                                )}
                                <div className="ml-auto flex gap-2">
                                    <button onClick={() => setEditing(null)} className="text-xs text-slate-500 px-2">Cancel</button>
                                    <Button size="sm" disabled={busy} onClick={saveItem}><Save size={13} /> {busy ? 'Saving…' : 'Save'}</Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {loading ? <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
                        : items.length === 0 && editing === null ? (
                            <p className="text-sm text-slate-400 text-center py-8">No canned responses yet — click <b>New</b> to create your first (e.g. FAQ answers, refund explanations).</p>
                        ) : (
                            Object.entries(grouped).map(([cat, list]) => (
                                <div key={cat}>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{cat} ({list.length})</div>
                                    <div className="space-y-1.5">
                                        {list.map(it => (
                                            <div key={it.id} className="bg-white border border-slate-200 rounded-xl px-3 py-2 flex items-start gap-2">
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-xs font-bold text-slate-700">{it.shortcut && <span className="font-mono text-purple-600 mr-1.5">/{it.shortcut}</span>}{it.title}</div>
                                                    <div className="text-[11px] text-slate-400 line-clamp-2">{it.text}</div>
                                                    {(it.attachments || []).length > 0 && (
                                                        <div className="flex gap-1.5 mt-1">
                                                            {(it.attachments || []).map((a, i) => isImg(a.type)
                                                                ? <img key={i} src={a.url} alt="" className="h-8 rounded border border-slate-100" />
                                                                : <span key={i} className="text-[10px] bg-slate-100 rounded px-1.5 py-0.5">{isAud(a.type) ? '🎤 voice' : '📎'}</span>)}
                                                        </div>
                                                    )}
                                                </div>
                                                <button onClick={() => startEdit(it)} className="p-1.5 text-slate-400 hover:text-purple-600 rounded"><Pencil size={13} /></button>
                                                <button onClick={() => deleteItem(it)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 size={13} /></button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                </div>
        </Wrapper>
    );
};

export default CannedManagerModal;
